import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { IAppointmentRequest } from "@/types/Entities";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { api } from "@/helper/api";
import dayjs from "dayjs";
import { Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RoundedContentBox from "@/components/general/roundedContentBox";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import YouTubePlayer from "@/components/general/youTubePlayer";
import CollapsibleAppointmentSection from "@/components/appointmentRequest/collapsibleAppointmentSection";
import OpenAppointmentRequestListItem from "@/components/appointmentRequest/openAppointmentRequestListItem";
import UpcomingAppointmentListItem from "@/components/appointmentRequest/upcomingAppointmentListItem";
import ManagerAppointmentRequestListItem from "@/components/appointmentRequest/managerAppointmentRequestListItem";
import styles from "@/components/appointmentRequest/appointmentListItem.module.css";

interface Props {
  subdomain: string;
}

export const BOOKED_APPOINTMENTS_SECTION_ID = "booked-appointments";
export const APPOINTMENT_REQUESTS_SECTION_ID = "appointment-requests";
export const APPOINTMENT_REQUESTS_COMPANY_SECTION_ID =
  "company-appointment-requests";

const SECTION_IDS = [
  APPOINTMENT_REQUESTS_SECTION_ID,
  BOOKED_APPOINTMENTS_SECTION_ID,
  APPOINTMENT_REQUESTS_COMPANY_SECTION_ID,
];

const openSectionIdsForHash = (hash: string) => {
  const targetSectionId = hash.replace(/^#/, "");
  return SECTION_IDS.includes(targetSectionId)
    ? [targetSectionId]
    : SECTION_IDS;
};

const UPCOMING_APPOINTMENT_STATUSES =
  "open,prefilled_by_user,in_progress,review_preparation_required,waiting_for_review,post_processing_required,completed";

export function AppointmentRequestOpen(props: Props) {
  const location = useLocation();
  const isManager = props.subdomain === "manage";
  const [appointmentRequest, setAppointmentRequest] = useState<
    IAppointmentRequest[] | any
  >([]);
  const [appointmentRequestsLoaded, setAppointmentRequestsLoaded] =
    useState<boolean>(false);
  const [upcomingAppointments, setUpcomingAppointments] = useState<any>([]);
  const [upcomingAppointmentsLoaded, setUpcomingAppointmentsLoaded] =
    useState<boolean>(false);
  const [
    manageDashboardUpcomingRequestsLoaded,
    setManageDashboardUpcomingRequestsLoaded,
  ] = useState<boolean>(!isManager);
  const [
    upcomingAppointmentRequestsManager,
    setUpcomingAppointmentRequestsManager,
  ] = useState<any>([]);
  const [notification, setNotification] = useState<string | null>(null);
  const { handleError, errorModal } = useErrorModal();
  const [youTubePlayerUrl, setYouTubePlayerUrl] = useState<string>("");
  const [youTubePlayerOpen, setYouTubePlayerOpen] = useState<boolean>(false);
  const [openSectionIds, setOpenSectionIds] = useState<string[]>(() =>
    openSectionIdsForHash(location.hash),
  );

  const { t } = useTranslation();
  usePageHeading(
    isManager
      ? t("heading_appointments_company_customer")
      : t("open_appointments"),
  );

  const theme = useTheme();
  const breakpointSm = useMediaQuery(theme.breakpoints.up("sm"));

  const loadAppointmentRequests = () => {
    api.genericApiRequest({
      entity: "appointmentRequest",
      method: "get",
      successHandler: (res: any) => {
        setAppointmentRequest(res.data.results);
        setAppointmentRequestsLoaded(true);
      },
      failHandler: (error: any) => {
        handleError(error);
        setAppointmentRequestsLoaded(true);
      },
    });
  };

  const loadUpcomingAppointments = () => {
    const dateToday = new Date().setHours(12, 0, 0, 0);
    api.genericApiRequest({
      entity: "appointment",
      method: "get",
      parametersToRender: {
        depth: "1",
        additionalUrlParameters: {
          status__in: UPCOMING_APPOINTMENT_STATUSES,
          datetime__gte: dayjs(dateToday).toISOString(),
        },
      },
      successHandler: (res: any) => {
        setUpcomingAppointments(res.data.results);
        setUpcomingAppointmentsLoaded(true);
      },
      failHandler: (error: any) => {
        handleError(error);
        setUpcomingAppointmentsLoaded(true);
      },
    });
  };

  useEffect(() => {
    loadAppointmentRequests();
  }, []);

  useEffect(() => {
    loadUpcomingAppointments();
  }, []);

  useEffect(() => {
    if (!isManager) {
      setManageDashboardUpcomingRequestsLoaded(true);
      return;
    }
    setManageDashboardUpcomingRequestsLoaded(false);
    api.genericApiRequest({
      entity: "dashboard",
      method: "get",
      parametersToRender: { depth: "1", additionalUrlParameters: {} },
      successHandler: (res: any) => {
        setUpcomingAppointmentRequestsManager(
          res.data?.upcoming_appointment_requests ?? [],
        );
        setManageDashboardUpcomingRequestsLoaded(true);
      },
      failHandler: (error: any) => {
        handleError(error);
        setManageDashboardUpcomingRequestsLoaded(true);
      },
    });
  }, [props.subdomain]);

  useEffect(() => {
    setOpenSectionIds(openSectionIdsForHash(location.hash));
  }, [location.key]);

  const isSectionOpen = (sectionId: string) =>
    openSectionIds.includes(sectionId);

  const toggleSection = (sectionId: string) => {
    setOpenSectionIds((current) =>
      current.includes(sectionId)
        ? current.filter((id) => id !== sectionId)
        : [...current, sectionId],
    );
  };

  const allLoaded =
    appointmentRequestsLoaded &&
    upcomingAppointmentsLoaded &&
    manageDashboardUpcomingRequestsLoaded;

  useEffect(() => {
    const hash = location.hash.replace(/^#/, "");
    if (!hash || !allLoaded) return;

    const el = document.getElementById(hash);
    if (!el) return;

    const frameId = window.requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [
    location.pathname,
    location.hash,
    allLoaded,
    upcomingAppointmentRequestsManager.length,
    upcomingAppointments.length,
    appointmentRequest.length,
  ]);

  const handleAppointmentCancel = (item: any) => {
    api.genericApiRequest({
      entity: "appointment",
      method: "put",
      entityId: item.id,
      additionalRouteParts: { cancel: "/" },
      submitData: {},
      successHandler: () => {
        setNotification(t("appointment_cancel_success"));
        setUpcomingAppointments(
          upcomingAppointments.filter(
            (appointment: any) => appointment.id !== item.id,
          ),
        );
        loadAppointmentRequests();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const handleAppointmentRequestDecline = (item: any) => {
    api.genericApiRequest({
      entity: "appointmentRequest",
      method: "put",
      entityId: item.id,
      additionalRouteParts: { withdraw: "/" },
      submitData: {},
      successHandler: () => {
        setNotification(t("appointment_request_decline_success"));
        setAppointmentRequest(
          appointmentRequest.filter((request: any) => request.id !== item.id),
        );
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const playVideo = (url: string) => {
    setYouTubePlayerUrl(url);
    setYouTubePlayerOpen(true);
  };

  const openAppointmentRequests: any[] = appointmentRequest ?? [];
  const bookedAppointments: any[] = (upcomingAppointments ?? []).filter(
    (item: any) =>
      Array.isArray(item?.examination_set) && item.examination_set.length !== 0,
  );
  const managerAppointmentRequests: any[] = isManager
    ? (upcomingAppointmentRequestsManager ?? [])
    : [];

  const showNoUpcomingMessage =
    allLoaded &&
    openAppointmentRequests.length === 0 &&
    bookedAppointments.length === 0 &&
    managerAppointmentRequests.length === 0;

  return (
    <Grid container rowSpacing={"1rem"}>
      {showNoUpcomingMessage && (
        <Grid item xs={12}>
          <RoundedContentBox>
            <div className={styles.emptyHint} role="status">
              <InfoOutlinedIcon fontSize="small" />
              {t("no_upcoming_appointments")}
            </div>
          </RoundedContentBox>
        </Grid>
      )}

      {openAppointmentRequests.length !== 0 && (
        <Grid item xs={12}>
          <CollapsibleAppointmentSection
            domId={APPOINTMENT_REQUESTS_SECTION_ID}
            title={t("appointmentrequest_open")}
            icon={<EditCalendarOutlinedIcon fontSize="small" />}
            itemCount={openAppointmentRequests.length}
            open={isSectionOpen(APPOINTMENT_REQUESTS_SECTION_ID)}
            onToggle={() => toggleSection(APPOINTMENT_REQUESTS_SECTION_ID)}
            compact={!breakpointSm}
          >
            {openAppointmentRequests.map((item: any) => (
              <OpenAppointmentRequestListItem
                key={item.id}
                item={item}
                wide={breakpointSm}
                onDecline={handleAppointmentRequestDecline}
                onPlayVideo={playVideo}
              />
            ))}
          </CollapsibleAppointmentSection>
        </Grid>
      )}
      {bookedAppointments.length !== 0 && (
        <Grid item xs={12}>
          <CollapsibleAppointmentSection
            domId={BOOKED_APPOINTMENTS_SECTION_ID}
            title={
              isManager
                ? t("upcoming_appointments_company")
                : t("upcoming_appointments")
            }
            icon={<EventAvailableOutlinedIcon fontSize="small" />}
            itemCount={bookedAppointments.length}
            open={isSectionOpen(BOOKED_APPOINTMENTS_SECTION_ID)}
            onToggle={() => toggleSection(BOOKED_APPOINTMENTS_SECTION_ID)}
            compact={!breakpointSm}
          >
            {bookedAppointments.map((item: any) => (
              <UpcomingAppointmentListItem
                key={item.id}
                item={item}
                wide={breakpointSm}
                onCancel={handleAppointmentCancel}
                onPlayVideo={playVideo}
              />
            ))}
          </CollapsibleAppointmentSection>
        </Grid>
      )}
      {managerAppointmentRequests.length !== 0 && (
        <Grid item xs={12}>
          <CollapsibleAppointmentSection
            domId={APPOINTMENT_REQUESTS_COMPANY_SECTION_ID}
            title={t("upcoming_appointments_customer")}
            icon={<GroupsOutlinedIcon fontSize="small" />}
            itemCount={managerAppointmentRequests.length}
            open={isSectionOpen(APPOINTMENT_REQUESTS_COMPANY_SECTION_ID)}
            onToggle={() =>
              toggleSection(APPOINTMENT_REQUESTS_COMPANY_SECTION_ID)
            }
            compact={!breakpointSm}
          >
            {managerAppointmentRequests.map((item: any) => (
              <ManagerAppointmentRequestListItem
                key={item.id ?? `${item.title}-${item.start_day}`}
                item={item}
              />
            ))}
          </CollapsibleAppointmentSection>
        </Grid>
      )}
      {notification && (
        <GenericNotification
          message={notification}
          variant="success"
          handleCloseSnackbar={() => setNotification(null)}
        />
      )}
      {errorModal}
      <YouTubePlayer
        url={youTubePlayerUrl}
        isOpen={youTubePlayerOpen}
        onClose={() => setYouTubePlayerOpen(false)}
        requireConsent={true}
      />
    </Grid>
  );
}
